"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const GREETING = {
    role: 'assistant',
    content: "Hi! Ask me anything about this proposal - the options, the prices, or the demo. Tell me what you'd like changed too, and I'll pass it straight on.",
};
function PresentationAssistant({ apiBaseUrl, businessId, previewKey, pageName = 'Presentation' }) {
    const storeKey = `mango_assistant_${businessId}`;
    const [open, setOpen] = (0, react_1.useState)(false);
    const [messages, setMessages] = (0, react_1.useState)([GREETING]);
    const [activityId, setActivityId] = (0, react_1.useState)(null);
    const [draft, setDraft] = (0, react_1.useState)('');
    const [sending, setSending] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)('');
    const endRef = (0, react_1.useRef)(null);
    (0, react_1.useEffect)(() => {
        try {
            const saved = JSON.parse(sessionStorage.getItem(storeKey) || 'null');
            if (saved?.messages?.length) {
                setMessages(saved.messages);
                setActivityId(saved.activityId ?? null);
            }
        }
        catch {
            /* start fresh */
        }
    }, [storeKey]);
    (0, react_1.useEffect)(() => {
        try {
            sessionStorage.setItem(storeKey, JSON.stringify({ messages, activityId }));
        }
        catch {
            /* private mode */
        }
        endRef.current?.scrollIntoView({ block: 'end' });
    }, [messages, activityId, storeKey, open]);
    const send = async () => {
        const text = draft.trim();
        if (!text || sending)
            return;
        const next = [...messages, { role: 'user', content: text }];
        setMessages(next);
        setDraft('');
        setSending(true);
        setError('');
        try {
            const res = await fetch(`${apiBaseUrl}/crm/presentation/${businessId}/assistant`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Presentation-Key': previewKey },
                // The greeting is ours, not part of the conversation the model sees.
                body: JSON.stringify({ messages: next.filter((m) => m !== GREETING), activityId, pageName }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok)
                throw new Error(data.error || 'The assistant is unavailable right now.');
            setMessages([...next, { role: 'assistant', content: data.reply }]);
            setActivityId(data.activityId);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'The assistant is unavailable right now.');
        }
        finally {
            setSending(false);
        }
    };
    if (!open) {
        return ((0, jsx_runtime_1.jsx)("button", { type: "button", onClick: () => setOpen(true), "aria-label": "Ask about this proposal", style: {
                position: 'fixed', right: 16, bottom: 16, zIndex: 1000, display: 'flex', alignItems: 'center', gap: 8,
                padding: '12px 16px', borderRadius: 999, border: 'none', background: '#667eea', color: 'white',
                fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 6px 20px rgba(102,126,234,0.45)', fontFamily: 'inherit',
            }, children: "\uD83D\uDCAC Ask about this proposal" }));
    }
    return ((0, jsx_runtime_1.jsxs)("div", { role: "dialog", "aria-label": "Proposal assistant", style: {
            position: 'fixed', right: 12, bottom: 12, zIndex: 1000, width: 'min(380px, calc(100vw - 24px))', height: 'min(520px, calc(100vh - 100px))',
            display: 'flex', flexDirection: 'column', background: 'white', borderRadius: 16, boxShadow: '0 12px 40px rgba(0,0,0,0.25)', overflow: 'hidden', fontFamily: 'inherit',
        }, children: [(0, jsx_runtime_1.jsxs)("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: '#667eea', color: 'white' }, children: [(0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsx)("div", { style: { fontWeight: 700, fontSize: 14 }, children: "\uD83D\uDCAC About your proposal" }), (0, jsx_runtime_1.jsx)("div", { style: { fontSize: 11, opacity: 0.9 }, children: "Everything you say here is passed on to us" })] }), (0, jsx_runtime_1.jsx)("button", { type: "button", onClick: () => setOpen(false), "aria-label": "Close", style: { border: 'none', background: 'none', color: 'white', fontSize: 20, cursor: 'pointer' }, children: "\u00D7" })] }), (0, jsx_runtime_1.jsxs)("div", { style: { flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8, background: '#f8f9ff' }, children: [messages.map((m, i) => ((0, jsx_runtime_1.jsx)("div", { style: {
                            alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', padding: '8px 12px', borderRadius: 12, fontSize: 14, lineHeight: 1.45,
                            background: m.role === 'user' ? '#667eea' : 'white', color: m.role === 'user' ? 'white' : '#222',
                            border: m.role === 'user' ? 'none' : '1px solid #e5e7eb', whiteSpace: 'pre-wrap',
                        }, children: m.content }, i))), sending && (0, jsx_runtime_1.jsx)("div", { style: { fontSize: 13, color: '#888' }, children: "Typing\u2026" }), error && (0, jsx_runtime_1.jsx)("div", { style: { fontSize: 13, color: '#b91c1c' }, children: error }), (0, jsx_runtime_1.jsx)("div", { ref: endRef })] }), (0, jsx_runtime_1.jsxs)("form", { onSubmit: (e) => {
                    e.preventDefault();
                    send();
                }, style: { display: 'flex', gap: 6, padding: 10, borderTop: '1px solid #eee' }, children: [(0, jsx_runtime_1.jsx)("input", { value: draft, onChange: (e) => setDraft(e.target.value), placeholder: "Ask a question or tell us what you'd change\u2026", maxLength: 2000, style: { flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd', fontSize: 14, fontFamily: 'inherit' } }), (0, jsx_runtime_1.jsx)("button", { type: "submit", disabled: sending || !draft.trim(), style: { padding: '0 14px', borderRadius: 10, border: 'none', background: sending || !draft.trim() ? '#ccc' : '#667eea', color: 'white', fontWeight: 700, cursor: 'pointer' }, children: "Send" })] })] }));
}
exports.default = PresentationAssistant;
